import { useMemo } from 'react';
import type { OrderItem } from '../graphql/query/getOrder';
import { useWorkflow } from '../workflow/hooks';
import { getShipmentIndexForItem } from '../utils/shipmentScopeUtils';
import { getBaseItemId } from '../utils/itemKey';

/**
 * Hook to calculate shipment-specific quantity for an item
 */
export const useShipmentQuantity = (itemId?: string, item?: OrderItem) => {
  const { order, data } = useWorkflow();

  return useMemo(() => {
    if (!itemId || !item?.quantity?.remaining || !order?.shipments) {
      return item?.quantity?.remaining ?? 0;
    }

    // For compound item IDs, extract the base item ID to look up shipment info
    const baseItemId = getBaseItemId(itemId);

    try {
      // Extract tracking number from workflow data
      const trackingNumber =
        data?.items?.[itemId]?.itemSelection?.trackingNumber;

      // If tracking number is available, use it to get the specific shipment
      if (trackingNumber) {
        const shipmentIndex = getShipmentIndexForItem(
          order,
          baseItemId,
          trackingNumber,
        );

        if (shipmentIndex !== undefined && order.shipments[shipmentIndex]) {
          const targetShipment = order.shipments[shipmentIndex];
          const shipmentItemInfo = targetShipment.itemsInfo?.find(
            (info) => info.itemId === baseItemId,
          );

          if (shipmentItemInfo) {
            return shipmentItemInfo.quantity;
          }
        }
      }

      // If no tracking number, find the first shipment containing this item
      // This provides shipment-specific quantities from the start
      for (const shipment of order.shipments) {
        const shipmentItemInfo = shipment.itemsInfo?.find(
          (info) => info.itemId === baseItemId,
        );
        if (shipmentItemInfo) {
          return shipmentItemInfo.quantity;
        }
      }
    } catch {
      // If any error occurs, fall back to the original quantity
      // Silently fall back without logging to avoid console warnings
    }

    return item.quantity.remaining;
  }, [itemId, item?.quantity?.remaining, order, data]);
};
